import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, Button, Row, Col, Popconfirm, Flex, Card, Tabs, Select, InputNumber, Typography, Drawer } from 'antd';
import Table from 'components/Common/Table';
import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons"
import * as actions from "store/actions/AuthActions";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from 'NotificationContext';
import AddLeadsDrawer from 'components/Campaign/AddLeadsDrawer';
import AddContactsDrawer from 'components/Campaign/AddContactsDrawer';
import AddDealsDrawer from 'components/Campaign/AddDealsDrawer';
import ImportLeadsDrawer from 'components/Campaign/ImportLeadsDrawer';
import ImportContactsDrawer from 'components/Campaign/ImportContactsDrawer';
import UTMLinkGenerator from 'components/Campaign/UTMLinkGenerator';

import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text } = Typography;

const CampaignForm = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [campaignLeads, setCampaignLeads] = useState([]);
    const [campaignContacts, setCampaignContacts] = useState([]);
    const [campaignDeals, setCampaignDeals] = useState([]);
    const [parentCampaignDeals, setParentCampaignDeals] = useState([]);
    const [childCampaigns, setChildCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [paymentProducts, setPaymentProducts] = useState([]);

    // Drawer states
    const [addLeadsDrawerVisible, setAddLeadsDrawerVisible] = useState(false);
    const [importLeadsDrawerVisible, setImportLeadsDrawerVisible] = useState(false);
    const [addContactsDrawerVisible, setAddContactsDrawerVisible] = useState(false);
    const [importContactsDrawerVisible, setImportContactsDrawerVisible] = useState(false);
    const [addDealsDrawerVisible, setAddDealsDrawerVisible] = useState(false);
    const [childCampaignModalVisible, setChildCampaignModalVisible] = useState(false);
    const [childCampaignForm] = Form.useForm();

    const notify = useNotification();

    const saveCampaign = async (values, shouldCreateNew = false) => {
        console.log('Received values:', values);
        setLoading(true);

        // Format dates before sending
        const formattedValues = { ...values };
        if (formattedValues.start_date) {
            formattedValues.start_date = dayjs(formattedValues.start_date).format('YYYY-MM-DD');
        }
        if (formattedValues.end_date) {
            formattedValues.end_date = dayjs(formattedValues.end_date).format('YYYY-MM-DD');
        }

        if (id) {
            const response = await actions.updateCampaign(formattedValues, id);
            if (response) {
                notify("success", "Campaign updated successfully");
                if (shouldCreateNew) {
                    form.resetFields();
                    navigate('/campaigns/create');
                }
            }
        } else {
            const response = await actions.addCampaign(formattedValues);
            if (response && response.status === 201) {
                notify("success", "Campaign created successfully");
                if (shouldCreateNew) {
                    form.resetFields();
                } else {
                    navigate('/campaigns/edit/' + response.data['id']);
                }
            }
        }
        setLoading(false);
    };

    const handleSaveAndNew = async () => {
        try {
            const values = await form.validateFields();
            await saveCampaign(values, true);
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const loadCampaign = async () => {
        if (id) {
            const response = await actions.retrieveCampaign(id);
            if (response) {
                const thisInstance = { ...response }
                let date_fields = ['start_date', 'end_date']
                for (let field of date_fields) {
                    thisInstance[field] = thisInstance[field] ? dayjs(thisInstance[field]) : null;
                }
                form.setFieldsValue(thisInstance);
                setCampaignLeads(response?.campaign_leads || []);
                setCampaignContacts(response?.campaign_contacts || []);
                setCampaignDeals(response?.campaign_deals || []);
                setChildCampaigns(response?.child_campaigns || []);

                // Load parent campaign deals if this is a child campaign
                if (response?.parent_campaign) {
                    const parentResponse = await actions.retrieveCampaign(response.parent_campaign);
                    if (parentResponse) {
                        const parentDealIds = (parentResponse?.campaign_deals || []).map(cd => cd.deal);
                        setParentCampaignDeals(parentDealIds);
                    }
                } else {
                    setParentCampaignDeals([]);
                }
            }
        }
        setLoading(false);
    }

    const loadEmployees = async () => {
        const response = await actions.loadEmployees();
        console.log('Employees response:', response);
        if (response && response.results) {
            console.log('Employees data:', response.results);
            setEmployees(response.results);
        }
    }

    const loadPaymentProducts = async () => {
        const response = await actions.loadProductPaymentProducts();
        if (response && response.data) {
            setPaymentProducts(Array.isArray(response.data) ? response.data : response.data.results || []);
        }
    }

    useEffect(() => {
        loadEmployees();
        loadPaymentProducts();
        if (id) {
            loadCampaign()
        } else {
            // Reset form when on create page
            form.resetFields();
            setCampaignLeads([]);
            setCampaignContacts([]);
            setCampaignDeals([]);
            setChildCampaigns([]);
            setParentCampaignDeals([]);
        }
    }, [id]);

    const handleRemoveLead = async (leadId) => {
        const response = await actions.removeCampaignLead(leadId);
        if (response) {
            setCampaignLeads(campaignLeads.filter(item => item.id !== leadId));
            notify("success", "Lead removed successfully");
        }
    }

    const handleRemoveContact = async (contactId) => {
        const response = await actions.removeCampaignContact(contactId);
        if (response) {
            setCampaignContacts(campaignContacts.filter(item => item.id !== contactId));
            notify("success", "Contact removed successfully");
        }
    }

    const handleRemoveDeal = async (dealId) => {
        const response = await actions.removeCampaignDeal(dealId);
        if (response) {
            setCampaignDeals(campaignDeals.filter(item => item.id !== dealId));
            notify("success", "Deal removed successfully");
        }
    }

    const handleCreateChildCampaign = async () => {
        try {
            const values = await childCampaignForm.validateFields();

            // Format dates before sending
            const formattedValues = { ...values, parent_campaign: id };
            if (formattedValues.start_date) {
                formattedValues.start_date = dayjs(formattedValues.start_date).format('YYYY-MM-DD');
            }
            if (formattedValues.end_date) {
                formattedValues.end_date = dayjs(formattedValues.end_date).format('YYYY-MM-DD');
            }

            const response = await actions.addCampaign(formattedValues);
            if (response && response.status === 201) {
                notify("success", "Child campaign created successfully");
                setChildCampaignModalVisible(false);
                childCampaignForm.resetFields();
                // Reload campaign to get updated child campaigns
                loadCampaign();
            }
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            console.error('Child campaign creation error:', error);
            notify("error", "Failed to create child campaign");
        }
    }

    // Stats calculation
    const revenueFromClosedDeals = (form.getFieldValue('revenue_from_closed_deals')) || 0;
    const pipelineAmount = (form.getFieldValue('pipeline_amount')) || 0;
    const actualCost = form.getFieldValue('actual_cost') || 0;

    const campaignLeadColumns = [
        {
            title: 'Lead Name',
            dataIndex: 'lead_name',
            key: 'lead_name',
        },
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'company',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Member Status',
            dataIndex: 'member_status_display',
            key: 'member_status_display',
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            render: (_, record) => {
                return (
                    <Flex gap="middle">
                        <Popconfirm title="Sure to remove?" onConfirm={() => handleRemoveLead(record.id)}>
                            <DeleteOutlined />
                        </Popconfirm>
                    </Flex>
                );
            },
        },
    ];

    const campaignContactColumns = [
        {
            title: 'Contact Name',
            dataIndex: 'contact_name',
            key: 'contact_name',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Account',
            dataIndex: 'account_name',
            key: 'account_name',
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            render: (_, record) => {
                return (
                    <Flex gap="middle">
                        <Popconfirm title="Sure to remove?" onConfirm={() => handleRemoveContact(record.id)}>
                            <DeleteOutlined />
                        </Popconfirm>
                    </Flex>
                );
            },
        },
    ];

    const campaignDealColumns = [
        {
            title: 'Deal Name',
            dataIndex: 'deal_name',
            key: 'deal_name',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (text) => `Rs. ${text || 0}`,
        },
        {
            title: 'Stage',
            dataIndex: 'stage_display',
            key: 'stage_display',
        },
        {
            title: 'Closing Date',
            dataIndex: 'closing_date',
            key: 'closing_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Actions',
            dataIndex: 'action',
            render: (_, record) => {
                return (
                    <Flex gap="middle">
                        <Popconfirm title="Sure to remove?" onConfirm={() => handleRemoveDeal(record.id)}>
                            <DeleteOutlined />
                        </Popconfirm>
                    </Flex>
                );
            },
        },
    ];

    const childCampaignColumns = [
        {
            title: 'Campaign Name',
            dataIndex: 'campaign_name',
            key: 'campaign_name',
            render: (text, record) => (
                <Typography.Link onClick={() => navigate(`/campaigns/edit/${record.id}`)}>{text}</Typography.Link>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type_display',
            key: 'type_display',
        },
        {
            title: 'Status',
            dataIndex: 'status_display',
            key: 'status_display',
        },
        {
            title: 'Expected Revenue',
            dataIndex: 'expected_revenue',
            key: 'expected_revenue',
            render: (text) => text ? `Rs. ${parseFloat(text).toLocaleString()}` : '-',
        },
        {
            title: 'Budget Cost',
            dataIndex: 'budget_cost',
            key: 'budget_cost',
            render: (text) => text ? `Rs. ${parseFloat(text).toLocaleString()}` : '-',
        },
        {
            title: 'Start Date',
            dataIndex: 'start_date',
            key: 'start_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'End Date',
            dataIndex: 'end_date',
            key: 'end_date',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
        },
    ];

    const tabElements = [
        {
            label: `Notes`,
            key: '1',
            children:
                <div>
                    <Form.Item name="notes" style={{ marginBottom: 0 }}>
                        <TextArea rows={4} placeholder="Add a note..." />
                    </Form.Item>
                    <Button
                        type="primary"
                        style={{ marginTop: 16 }}
                        onClick={() => form.submit()}
                    >
                        Save Note
                    </Button>
                </div>,
        },
        {
            label: `Child Campaigns`,
            key: '2',
            children:
                <div>
                    <Flex justify="end">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            style={{ marginBottom: 16 }}
                            onClick={() => setChildCampaignModalVisible(true)}
                        >
                            New Child Campaign
                        </Button>
                    </Flex>
                    <Table
                        dataSource={childCampaigns}
                        columns={childCampaignColumns}
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                    />
                </div>,
        },
        {
            label: `Leads`,
            key: '3',
            children:
                <div>
                    <Flex justify="end" gap="small" style={{ marginBottom: 16 }}>
                        <Button type="default" onClick={() => setAddLeadsDrawerVisible(true)}>Add Leads</Button>
                        <Button type="default" icon={<UploadOutlined />} onClick={() => setImportLeadsDrawerVisible(true)}>
                            Import Leads
                        </Button>
                    </Flex>
                    <Table
                        dataSource={campaignLeads}
                        columns={campaignLeadColumns}
                        rowClassName="editable-row"
                        size="small"
                        pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} leads`,
                        }}
                        rowKey="id"
                    />
                </div>,
        },
        {
            label: `Deals`,
            key: '4',
            children:
                <div>
                    <Flex justify="end" gap="small">
                        <Button type="default" onClick={() => setAddDealsDrawerVisible(true)}>Assign</Button>
                        <Button type="primary" onClick={() => navigate('/deals/new')}>New Deal</Button>
                    </Flex>
                    <Table
                        dataSource={campaignDeals}
                        columns={campaignDealColumns}
                        rowClassName="editable-row"
                        size="small"
                        pagination={false}
                        style={{ marginTop: 16 }}
                    />
                </div>,
        },
        {
            label: `Contacts`,
            key: '5',
            children:
                <div>
                    <Flex justify="end" gap="small" style={{ marginBottom: 16 }}>
                        <Button type="default" onClick={() => setAddContactsDrawerVisible(true)}>Add Contacts</Button>
                        <Button type="default" icon={<UploadOutlined />} onClick={() => setImportContactsDrawerVisible(true)}>
                            Import Contacts
                        </Button>
                    </Flex>
                    <Table
                        dataSource={campaignContacts}
                        columns={campaignContactColumns}
                        rowClassName="editable-row"
                        size="small"
                        pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacts`,
                        }}
                        rowKey="id"
                    />
                </div>,
        },
        {
            label: `UTM Links`,
            key: '6',
            children: <UTMLinkGenerator campaignId={id} campaignName={form.getFieldValue('campaign_name')} />,
        },
    ]

    return (
        <>
            <div className='custom-scroll-div' style={{ overflowY: "scroll", maxHeight: "90vh", padding: 10, paddingBottom: 30 }}>
                <Form form={form} onFinish={saveCampaign} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} labelAlign="left">
                    <Flex justify='space-between' align='center' style={{ margin: "10px auto" }}>
                        <Typography.Title level={3}>{id ? 'Edit' : 'Create'} Campaign</Typography.Title>
                        <Flex gap='middle'>
                            <Button onClick={() => navigate('/campaigns')}>Cancel</Button>
                            <Button type="default" onClick={handleSaveAndNew} loading={loading}>Save and New</Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Save
                            </Button>
                        </Flex>
                    </Flex>

                    {/* Overview Statistics - Displayed at the top */}
                    {id && (
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={6}>
                                <Card style={{ height: 160 }}>
                                    <div style={{ textAlign: 'left' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Total Count</Text>
                                        <div style={{ fontSize: 48, fontWeight: 'bold', margin: '10px 0', lineHeight: 1 }}>{campaignLeads.length + campaignContacts.length}</div>
                                        <div style={{ marginBottom: 8 }}>
                                            <Text style={{ fontSize: 13, color: '#666' }}>{campaignLeads.length} Leads</Text>
                                            <br />
                                            <Text style={{ fontSize: 13, color: '#666' }}>{campaignContacts.length} Contacts</Text>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            <Col span={18}>
                                <Card style={{ height: 160 }}>
                                    <Flex justify="space-between" align="flex-start">
                                        <div style={{ flex: 1, textAlign: 'left' }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Actual Cost</Text>
                                            <div style={{ fontSize: 24, fontWeight: 'bold', marginTop: 8 }}>Rs. {parseFloat(actualCost || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                        </div>
                                        <div style={{ flex: 1, textAlign: 'left', paddingLeft: 20 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Revenue</Text>
                                            <div style={{ fontSize: 24, fontWeight: 'bold', marginTop: 8 }}>Rs. {parseFloat(revenueFromClosedDeals || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                            <Text type="secondary" style={{ fontSize: 11, color: '#999' }}>From {campaignDeals.filter(d => d.stage === 'CLOSED_WON').length} Closed Deals</Text>
                                        </div>
                                        <div style={{ flex: 1, textAlign: 'left', paddingLeft: 20 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>In Pipeline</Text>
                                            <div style={{ fontSize: 24, fontWeight: 'bold', marginTop: 8 }}>Rs. {parseFloat(pipelineAmount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                            <Text type="secondary" style={{ fontSize: 11, color: '#999' }}>From {campaignDeals.filter(d => !['CLOSED_WON', 'CLOSED_LOST', 'CLOSED_LOST_TO_COMPETITION'].includes(d.stage)).length} Open Deals</Text>
                                        </div>
                                    </Flex>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col span={12}>
                            <Card title="Campaign Information" bordered={false}>
                                <Form.Item label="Campaign Owner" name="employee_campaign_owner">
                                    <Select
                                        showSearch
                                        placeholder="Select campaign owner"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={employees.map(employee => {
                                            // Clean up user_name by removing "None" and extra spaces
                                            const userName = employee.user_name || '';
                                            const cleanName = userName.split(' ')
                                                .filter(part => part && part !== 'None')
                                                .join(' ')
                                                .trim();

                                            return {
                                                value: employee.id,
                                                label: `${cleanName || 'Unknown'} (${employee.employee_id || 'N/A'})`
                                            };
                                        })}
                                    />
                                </Form.Item>
                                <Form.Item label="Campaign Name" name="campaign_name" rules={[{ required: true, message: 'Please input campaign name!' }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Type" name="type" rules={[{ required: true, message: 'Please select type!' }]}>
                                    <Select>
                                        <Select.Option value="NONE">-None-</Select.Option>
                                        <Select.Option value="CONFERENCE">Conference</Select.Option>
                                        <Select.Option value="WEBINAR">Webinar</Select.Option>
                                        <Select.Option value="TRADE_SHOW">Trade Show</Select.Option>
                                        <Select.Option value="PUBLIC_RELATIONS">Public Relations</Select.Option>
                                        <Select.Option value="PARTNERS">Partners</Select.Option>
                                        <Select.Option value="REFERRAL_PROGRAM">Referral Program</Select.Option>
                                        <Select.Option value="ADVERTISEMENT">Advertisement</Select.Option>
                                        <Select.Option value="BANNER_ADS">Banner Ads</Select.Option>
                                        <Select.Option value="DIRECT_MAIL">Direct mail</Select.Option>
                                        <Select.Option value="EMAIL">Email</Select.Option>
                                        <Select.Option value="TELEMARKETING">Telemarketing</Select.Option>
                                        <Select.Option value="OTHERS">Others</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Status" name="status">
                                    <Select>
                                        <Select.Option value="NONE">-None-</Select.Option>
                                        <Select.Option value="PLANNING">Planning</Select.Option>
                                        <Select.Option value="ACTIVE">Active</Select.Option>
                                        <Select.Option value="INACTIVE">Inactive</Select.Option>
                                        <Select.Option value="COMPLETE">Complete</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Product" name="product">
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select product (optional)"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={paymentProducts.map(p => ({
                                            value: p.id,
                                            label: p.name
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Start Date" name="start_date">
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item
                                    label="End Date"
                                    name="end_date"
                                    dependencies={['start_date']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const startDate = getFieldValue('start_date');
                                                if (!value || !startDate) {
                                                    return Promise.resolve();
                                                }
                                                if (value.isBefore(startDate) || value.isSame(startDate, 'day')) {
                                                    return Promise.reject(new Error('Start Date must be earlier than End Date'));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                </Form.Item>
                            </Card>
                        </Col>
                        <Col span={12} style={{ padding: "0px 0px 10px 10px" }}>
                            <Card title="Campaign Metrics" bordered={false}>
                                <Form.Item label="Expected Revenue" name="expected_revenue">
                                    <InputNumber
                                        prefix="Rs."
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                                <Form.Item label="Budgeted Cost" name="budget_cost">
                                    <InputNumber
                                        prefix="Rs."
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                                <Form.Item label="Actual Cost" name="actual_cost">
                                    <InputNumber
                                        prefix="Rs."
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                    />
                                </Form.Item>
                                <Form.Item label="Expected Response" name="expected_response">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                    />
                                </Form.Item>
                                <Form.Item label="Numbers sent" name="numbers_sent">
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                    />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>
                    <Row style={{ padding: "10px 0px 0px 0px" }}>
                        <Col span={24}>
                            <Card title="Description Information" bordered={false}>
                                <Form.Item label="Description" name="description" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                                    <TextArea rows={4} />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>
                    {id && (
                        <Row style={{ padding: "10px 0px 0px 0px" }}>
                            <Col span={24}>
                                <div className="site-card-border-less-wrapper">
                                    <Card bordered={false}>
                                        <Tabs
                                            defaultActiveKey="1"
                                            items={tabElements}
                                        />
                                    </Card>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Form>
            </div>

            {/* Add Leads Drawer */}
            <AddLeadsDrawer
                visible={addLeadsDrawerVisible}
                onClose={() => setAddLeadsDrawerVisible(false)}
                campaignId={id}
                existingLeadIds={campaignLeads.map(cl => cl.lead)}
                onSuccess={() => {
                    loadCampaign();
                }}
            />

            {/* Import Leads Drawer */}
            <ImportLeadsDrawer
                visible={importLeadsDrawerVisible}
                onClose={() => setImportLeadsDrawerVisible(false)}
                campaignId={id}
                onSuccess={() => {
                    loadCampaign();
                }}
            />

            {/* Import Contacts Drawer */}
            <ImportContactsDrawer
                visible={importContactsDrawerVisible}
                onClose={() => setImportContactsDrawerVisible(false)}
                campaignId={id}
                onSuccess={() => {
                    loadCampaign();
                }}
            />

            {/* Add Contacts Drawer */}
            <AddContactsDrawer
                visible={addContactsDrawerVisible}
                onClose={() => setAddContactsDrawerVisible(false)}
                campaignId={id}
                existingContactIds={campaignContacts.map(cc => cc.contact)}
                onSuccess={() => {
                    loadCampaign();
                }}
            />

            {/* Add Deals Drawer */}
            <AddDealsDrawer
                visible={addDealsDrawerVisible}
                onClose={() => setAddDealsDrawerVisible(false)}
                campaignId={id}
                existingDealIds={campaignDeals.map(cd => cd.deal)}
                parentCampaignDealIds={parentCampaignDeals}
                onSuccess={() => {
                    loadCampaign();
                }}
            />

            {/* Child Campaign Modal */}
            <Drawer
                title="Create Child Campaign"
                open={childCampaignModalVisible}
                onClose={() => {
                    setChildCampaignModalVisible(false);
                    childCampaignForm.resetFields();
                }}
                width={600}
                footer={
                    <Flex justify="end" gap="middle">
                        <Button onClick={() => {
                            setChildCampaignModalVisible(false);
                            childCampaignForm.resetFields();
                        }}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={handleCreateChildCampaign}>
                            Save
                        </Button>
                    </Flex>
                }
            >
                <Form
                    form={childCampaignForm}
                    layout="vertical"
                >
                    <Form.Item
                        label="Campaign Name"
                        name="campaign_name"
                        rules={[{ required: true, message: 'Please enter campaign name' }]}
                    >
                        <Input placeholder="Enter campaign name" />
                    </Form.Item>

                    <Form.Item
                        label="Campaign Owner"
                        name="employee_campaign_owner"
                    >
                        <Select
                            showSearch
                            placeholder="Select campaign owner"
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={employees.map(employee => {
                                // Clean up user_name by removing "None" and extra spaces
                                const userName = employee.user_name || '';
                                const cleanName = userName.split(' ')
                                    .filter(part => part && part !== 'None')
                                    .join(' ')
                                    .trim();

                                return {
                                    value: employee.id,
                                    label: `${cleanName || 'Unknown'} (${employee.employee_id || 'N/A'})`
                                };
                            })}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Type"
                        name="type"
                        rules={[{ required: true, message: 'Please select type' }]}
                    >
                        <Select>
                            <Select.Option value="NONE">-None-</Select.Option>
                            <Select.Option value="CONFERENCE">Conference</Select.Option>
                            <Select.Option value="WEBINAR">Webinar</Select.Option>
                            <Select.Option value="TRADE_SHOW">Trade Show</Select.Option>
                            <Select.Option value="PUBLIC_RELATIONS">Public Relations</Select.Option>
                            <Select.Option value="PARTNERS">Partners</Select.Option>
                            <Select.Option value="REFERRAL_PROGRAM">Referral Program</Select.Option>
                            <Select.Option value="ADVERTISEMENT">Advertisement</Select.Option>
                            <Select.Option value="BANNER_ADS">Banner Ads</Select.Option>
                            <Select.Option value="DIRECT_MAIL">Direct mail</Select.Option>
                            <Select.Option value="EMAIL">Email</Select.Option>
                            <Select.Option value="TELEMARKETING">Telemarketing</Select.Option>
                            <Select.Option value="OTHERS">Others</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Status"
                        name="status"
                    >
                        <Select>
                            <Select.Option value="NONE">-None-</Select.Option>
                            <Select.Option value="PLANNING">Planning</Select.Option>
                            <Select.Option value="ACTIVE">Active</Select.Option>
                            <Select.Option value="INACTIVE">Inactive</Select.Option>
                            <Select.Option value="COMPLETE">Complete</Select.Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Start Date"
                                name="start_date"
                            >
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="End Date"
                                name="end_date"
                                dependencies={['start_date']}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const startDate = getFieldValue('start_date');
                                            if (!value || !startDate) {
                                                return Promise.resolve();
                                            }
                                            if (value.isBefore(startDate) || value.isSame(startDate, 'day')) {
                                                return Promise.reject(new Error('Start Date must be earlier than End Date'));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Expected Revenue"
                        name="expected_revenue"
                    >
                        <InputNumber
                            prefix="Rs."
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Budgeted Cost"
                        name="budget_cost"
                    >
                        <InputNumber
                            prefix="Rs."
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <TextArea rows={3} placeholder="Enter description" />
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
}

export default CampaignForm;
